import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { convertScoreToFive } from "@/lib/utils";

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: Array<{
    id: string;
    nom: string;
    score: number;
    decision: string;
    created_at: string;
  }>;
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface AppelOffres {
  id: string;
  titre: string;
  reference: string;
}

export const useRankingPdfExport = () => {
  // Radar chart drawing function
  const drawRadarChart = (
    doc: jsPDF,
    centerX: number,
    centerY: number,
    radius: number,
    criteriaLabels: string[],
    companiesData: { name: string; scores: number[]; color: [number, number, number] }[]
  ) => {
    if (criteriaLabels.length < 3) return;

    const angleStep = (2 * Math.PI) / criteriaLabels.length;
    const levels = 5;

    // Draw colored zone backgrounds
    // Green zone (70-100)
    doc.setFillColor(220, 252, 231);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius;
      const innerRadius = radius * 0.7;
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Yellow zone (40-70)
    doc.setFillColor(254, 249, 195);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.7;
      const innerRadius = radius * 0.4;
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Red zone (0-40)
    doc.setFillColor(254, 226, 226);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.4;
      doc.triangle(
        centerX, centerY,
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Draw grid
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const points: [number, number][] = [];
      for (let i = 0; i < criteriaLabels.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        points.push([centerX + levelRadius * Math.cos(angle), centerY + levelRadius * Math.sin(angle)]);
      }
      for (let i = 0; i < points.length; i++) {
        const next = (i + 1) % points.length;
        doc.line(points[i][0], points[i][1], points[next][0], points[next][1]);
      }
    }

    // Draw axis lines
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      doc.line(centerX, centerY, centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle));
    }

    // Draw each company's data polygon
    for (const company of companiesData) {
      const dataPoints: [number, number][] = [];
      for (let i = 0; i < company.scores.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        const value = (company.scores[i] / 100) * radius;
        dataPoints.push([centerX + value * Math.cos(angle), centerY + value * Math.sin(angle)]);
      }

      // Draw outline
      doc.setDrawColor(...company.color);
      doc.setLineWidth(1);
      for (let i = 0; i < dataPoints.length; i++) {
        const next = (i + 1) % dataPoints.length;
        doc.line(dataPoints[i][0], dataPoints[i][1], dataPoints[next][0], dataPoints[next][1]);
      }

      // Draw data points
      doc.setFillColor(...company.color);
      for (const point of dataPoints) {
        doc.circle(point[0], point[1], 1.2, 'F');
      }
    }

    // Draw labels
    doc.setFontSize(6);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 6;
      const labelX = centerX + labelRadius * Math.cos(angle);
      const labelY = centerY + labelRadius * Math.sin(angle);
      let labelText = criteriaLabels[i];
      if (labelText.length > 12) labelText = labelText.substring(0, 11) + '...';
      let align: 'left' | 'center' | 'right' = 'center';
      let offsetX = 0;
      if (Math.cos(angle) > 0.3) { align = 'left'; offsetX = 2; }
      else if (Math.cos(angle) < -0.3) { align = 'right'; offsetX = -2; }
      doc.text(labelText, labelX + offsetX, labelY + 1, { align });
    }

    // Draw scale labels
    doc.setFontSize(5);
    doc.setTextColor(150, 150, 150);
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const scoreValue = (level / levels) * 100;
      doc.text(String(Math.round(scoreValue)), centerX + 2, centerY - levelRadius + 2);
    }
  };

  const exportRankingToPdf = async (
    appelOffre: AppelOffres,
    rankings: SocieteRanking[],
    topNForRadar: number = 5,
    selectedCompanyIds?: string[]
  ) => {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    // Colors
    const primaryColor: [number, number, number] = [59, 130, 246];
    const textColor: [number, number, number] = [30, 41, 59];
    const mutedColor: [number, number, number] = [100, 116, 139];
    
    let yPosition = 20;

    // ========== PAGE 1: HEADER & STATS ==========
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(...primaryColor);
    doc.text("Rapport de Ranking par Société", 105, yPosition, { align: "center" });
    yPosition += 10;
    
    // Subtitle with AO info
    doc.setFontSize(12);
    doc.setTextColor(...mutedColor);
    doc.text(`${appelOffre.reference} - ${appelOffre.titre}`, 105, yPosition, { align: "center" });
    yPosition += 8;
    
    doc.setFontSize(9);
    doc.text(`Généré le ${format(new Date(), "dd MMMM yyyy à HH:mm", { locale: fr })}`, 105, yPosition, { align: "center" });
    yPosition += 15;

    // Separator line
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;

    // ========== SUMMARY STATISTICS ==========
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("1. Statistiques Globales", 20, yPosition);
    yPosition += 10;

    const totalCandidatures = rankings.reduce((sum, r) => sum + r.nb_candidatures, 0);
    const averageScore = rankings.length > 0 
      ? rankings.reduce((sum, r) => sum + r.score_moyen, 0) / rankings.length 
      : 0;
    const bestScore = rankings.length > 0 ? rankings[0].score_moyen : 0;
    const bestCompany = rankings.length > 0 ? rankings[0].societe_nom : "-";
    
    const totalShortlist = rankings.reduce((sum, r) => sum + r.decision_counts.shortlist, 0);
    const totalConsider = rankings.reduce((sum, r) => sum + r.decision_counts.consider, 0);
    const totalReject = rankings.reduce((sum, r) => sum + r.decision_counts.reject, 0);

    // Stats boxes
    const statsData = [
      ["Nombre de sociétés", rankings.length.toString()],
      ["Total dossiers analysés", totalCandidatures.toString()],
      ["Score moyen global", `${averageScore.toFixed(1)}% (${convertScoreToFive(averageScore)}/5)`],
      ["Meilleur score", `${bestScore.toFixed(1)}% (${convertScoreToFive(bestScore)}/5)`],
      ["Meilleure société", bestCompany],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Indicateur", "Valeur"]],
      body: statsData,
      theme: "striped",
      headStyles: { 
        fillColor: primaryColor, 
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 80, halign: "center", fontStyle: "bold" }
      },
      margin: { left: 25, right: 25 }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;

    // ========== DECISION DISTRIBUTION ==========
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("2. Répartition des Décisions", 20, yPosition);
    yPosition += 10;

    const decisionData = [
      ["Shortlist", totalShortlist.toString(), `${((totalShortlist / totalCandidatures) * 100).toFixed(1)}%`],
      ["Consider", totalConsider.toString(), `${((totalConsider / totalCandidatures) * 100).toFixed(1)}%`],
      ["Reject", totalReject.toString(), `${((totalReject / totalCandidatures) * 100).toFixed(1)}%`],
      ["Total", totalCandidatures.toString(), "100%"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Décision", "Nombre", "Pourcentage"]],
      body: decisionData,
      theme: "striped",
      headStyles: { 
        fillColor: primaryColor, 
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50, halign: "center" },
        2: { cellWidth: 50, halign: "center" }
      },
      margin: { left: 25, right: 25 },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.row.index === 0) data.cell.styles.textColor = [34, 197, 94]; // green
          if (data.row.index === 1) data.cell.styles.textColor = [234, 179, 8]; // yellow
          if (data.row.index === 2) data.cell.styles.textColor = [239, 68, 68]; // red
          if (data.row.index === 3) data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;

    // ========== RANKING TABLE ==========
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("3. Classement des Sociétés", 20, yPosition);
    yPosition += 10;

    const rankingData = rankings.map((r, index) => [
      `#${index + 1}`,
      r.societe_nom,
      r.nb_candidatures.toString(),
      `${r.score_moyen.toFixed(1)}%`,
      convertScoreToFive(r.score_moyen),
      `${r.decision_counts.shortlist}/${r.decision_counts.consider}/${r.decision_counts.reject}`,
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Rang", "Société", "Dossiers", "Score", "Note /5", "S/C/R"]],
      body: rankingData,
      theme: "striped",
      headStyles: { 
        fillColor: primaryColor, 
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 15, halign: "center" },
        1: { cellWidth: 55 },
        2: { cellWidth: 20, halign: "center" },
        3: { cellWidth: 25, halign: "center" },
        4: { cellWidth: 20, halign: "center" },
        5: { cellWidth: 25, halign: "center" }
      },
      margin: { left: 20, right: 20 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 0) {
          data.cell.styles.fillColor = [254, 249, 195]; // light yellow for #1
        }
      }
    });

    // ========== PAGE 2: HEATMAP ==========
    doc.addPage("landscape");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("4. Matrice de Chaleur Comparative", 20, yPosition);
    yPosition += 5;
    
    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Scores par critère pour chaque société (tous les critères)", 20, yPosition);
    yPosition += 10;

    // Get all unique criteria
    const allCriteria = new Set<string>();
    rankings.forEach(r => {
      Object.keys(r.scores_criteres).forEach(c => allCriteria.add(c));
    });
    const criteriaList = Array.from(allCriteria);

    if (criteriaList.length > 0) {
      // Build heatmap data with % and score /5
      const heatmapHead = ["Société", "Score Global", ...criteriaList.map(c => 
        c.length > 12 ? c.substring(0, 12) + "..." : c
      )];
      
      const heatmapData = rankings.map(r => {
        const row = [
          r.societe_nom.length > 20 ? r.societe_nom.substring(0, 20) + "..." : r.societe_nom,
          `${r.score_moyen.toFixed(1)}%\n(${(r.score_moyen / 20).toFixed(1)}/5)`,
        ];
        criteriaList.forEach(criteria => {
          const score = r.scores_criteres[criteria]?.score || 0;
          row.push(`${score.toFixed(1)}%\n(${(score / 20).toFixed(1)}/5)`);
        });
        return row;
      });

      // Helper function to get color based on absolute value (0-100%)
      const getHeatmapColor = (value: number): { fill: [number, number, number], text: [number, number, number] } => {
        if (value >= 80) {
          return { fill: [34, 197, 94], text: [255, 255, 255] }; // green-500
        } else if (value >= 60) {
          return { fill: [74, 222, 128], text: [0, 0, 0] }; // green-400
        } else if (value >= 40) {
          return { fill: [250, 204, 21], text: [0, 0, 0] }; // yellow-400
        } else if (value >= 20) {
          return { fill: [251, 146, 60], text: [0, 0, 0] }; // orange-400
        } else {
          return { fill: [248, 113, 113], text: [255, 255, 255] }; // red-400
        }
      };

      autoTable(doc, {
        startY: yPosition,
        head: [heatmapHead],
        body: heatmapData,
        theme: "grid",
        headStyles: { 
          fillColor: primaryColor, 
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7,
          cellPadding: 2
        },
        styles: { fontSize: 7, cellPadding: 3, halign: "center", valign: "middle" },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
        },
        margin: { left: 10, right: 10 },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index > 0) {
            const cellText = data.cell.raw as string;
            // Extract the percentage value from the cell text (format: "XX.X%\n(X.X/5)")
            const match = cellText.match(/^([\d.]+)%/);
            const value = match ? parseFloat(match[1]) : 0;
            
            // Color coding based on absolute value thresholds
            const colors = getHeatmapColor(value);
            data.cell.styles.fillColor = colors.fill;
            data.cell.styles.textColor = colors.text;
          }
        }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;

      // Heatmap legend
      doc.setFontSize(8);
      doc.setTextColor(...textColor);
      doc.text("Légende:", 20, yPosition);
      
      const legendColors = [
        { label: "0-20%", color: [248, 113, 113] as [number, number, number] },
        { label: "20-40%", color: [251, 146, 60] as [number, number, number] },
        { label: "40-60%", color: [250, 204, 21] as [number, number, number] },
        { label: "60-80%", color: [74, 222, 128] as [number, number, number] },
        { label: "80-100%", color: [34, 197, 94] as [number, number, number] },
      ];
      
      let legendX = 45;
      legendColors.forEach(legend => {
        doc.setFillColor(...legend.color);
        doc.rect(legendX, yPosition - 3, 8, 4, 'F');
        doc.text(legend.label, legendX + 10, yPosition);
        legendX += 35;
      });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(...mutedColor);
      doc.text("Aucun critère détaillé disponible pour cette analyse", 20, yPosition);
    }

    // ========== PAGE 3: RADAR CHART COMPARISON ==========
    doc.addPage("portrait");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    const radarTitle = selectedCompanyIds && selectedCompanyIds.length > 0 
      ? `5. Comparaison Radar (${selectedCompanyIds.length} Sociétés Sélectionnées)`
      : `5. Comparaison Radar des Top ${topNForRadar} Sociétés`;
    doc.text(radarTitle, 20, yPosition);
    yPosition += 5;

    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Visualisation comparative des scores par critère", 20, yPosition);
    yPosition += 12;

    if (criteriaList.length >= 3 && rankings.length > 0) {
      // Company colors for the radar
      const companyColors: [number, number, number][] = [
        [59, 130, 246],   // blue
        [239, 68, 68],    // red
        [34, 197, 94],    // green
        [168, 85, 247],   // purple
        [249, 115, 22],   // orange
        [236, 72, 153],   // pink
        [20, 184, 166],   // teal
        [245, 158, 11],   // amber
        [99, 102, 241],   // indigo
        [107, 114, 128],  // gray
      ];

      // Filter rankings based on selected companies or use topN
      const filteredRankings = selectedCompanyIds && selectedCompanyIds.length > 0
        ? rankings.filter(r => selectedCompanyIds.includes(r.societe_id))
        : rankings.slice(0, topNForRadar);
      
      const companiesData = filteredRankings.map((r, index) => ({
        name: r.societe_nom,
        scores: criteriaList.map(c => r.scores_criteres[c]?.score || 0),
        color: companyColors[index % companyColors.length]
      }));
      
      const numCompanies = companiesData.length;

      // Adjust radar size based on number of companies (more companies = need more legend space)
      const radarCenterX = numCompanies > 5 ? 60 : 70;
      const radarCenterY = yPosition + 50;
      const radarRadius = numCompanies > 5 ? 35 : 40;

      drawRadarChart(doc, radarCenterX, radarCenterY, radarRadius, criteriaList, companiesData);

      // Draw legend on the right (adjust position for more companies)
      const legendX = numCompanies > 5 ? 115 : 130;
      let legendY = yPosition + 5;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textColor);
      doc.text('Légende des sociétés', legendX, legendY);
      legendY += 8;

      for (let i = 0; i < companiesData.length; i++) {
        const company = companiesData[i];
        
        // Color indicator line
        doc.setDrawColor(...company.color);
        doc.setLineWidth(2);
        doc.line(legendX, legendY - 1, legendX + 10, legendY - 1);
        
        // Dot
        doc.setFillColor(...company.color);
        doc.circle(legendX + 5, legendY - 1, 1.5, 'F');

        // Company name and score
        doc.setTextColor(...textColor);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        let displayName = company.name;
        if (displayName.length > 25) displayName = displayName.substring(0, 24) + '...';
        doc.text(`#${i + 1} ${displayName}`, legendX + 14, legendY);
        
        // Average score
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...mutedColor);
        doc.text(`Score: ${filteredRankings[i].score_moyen.toFixed(1)}%`, legendX + 14, legendY + 4);
        
        legendY += numCompanies > 5 ? 10 : 12;
      }

      // Zone legend
      legendY += 5;
      doc.setFontSize(8);
      doc.setTextColor(...textColor);
      doc.setFont('helvetica', 'bold');
      doc.text('Zones de performance', legendX, legendY);
      legendY += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      
      doc.setFillColor(220, 252, 231);
      doc.rect(legendX, legendY - 2, 8, 4, 'F');
      doc.setTextColor(34, 197, 94);
      doc.text('70-100% Excellent', legendX + 11, legendY + 1);
      legendY += 6;

      doc.setFillColor(254, 249, 195);
      doc.rect(legendX, legendY - 2, 8, 4, 'F');
      doc.setTextColor(234, 179, 8);
      doc.text('40-70% Moyen', legendX + 11, legendY + 1);
      legendY += 6;

      doc.setFillColor(254, 226, 226);
      doc.rect(legendX, legendY - 2, 8, 4, 'F');
      doc.setTextColor(239, 68, 68);
      doc.text('0-40% Faible', legendX + 11, legendY + 1);

      yPosition = radarCenterY + radarRadius + 20;
    } else {
      doc.setFontSize(10);
      doc.setTextColor(...mutedColor);
      doc.text("Pas assez de critères pour afficher le graphique radar (minimum 3)", 20, yPosition);
      yPosition += 20;
    }

    // ========== PAGE 4: DETAILED CRITERIA STATS ==========
    doc.addPage("portrait");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("6. Statistiques par Critère", 20, yPosition);
    yPosition += 10;

    if (criteriaList.length > 0) {
      const criteriaStats = criteriaList.map(criteria => {
        const scores = rankings
          .map(r => r.scores_criteres[criteria]?.score || 0)
          .filter(s => s > 0);
        
        const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
        const max = scores.length > 0 ? Math.max(...scores) : 0;
        const min = scores.length > 0 ? Math.min(...scores) : 0;
        
        return [
          criteria.length > 30 ? criteria.substring(0, 30) + "..." : criteria,
          `${avg.toFixed(1)}%`,
          `${max.toFixed(1)}%`,
          `${min.toFixed(1)}%`,
          scores.length.toString()
        ];
      });

      autoTable(doc, {
        startY: yPosition,
        head: [["Critère", "Moyenne", "Max", "Min", "# Évaluations"]],
        body: criteriaStats,
        theme: "striped",
        headStyles: { 
          fillColor: primaryColor, 
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 25, halign: "center" },
          2: { cellWidth: 25, halign: "center" },
          3: { cellWidth: 25, halign: "center" },
          4: { cellWidth: 25, halign: "center" }
        },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;
    }

    // ========== TOP PERFORMERS BY CRITERIA ==========
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("7. Meilleures Sociétés par Critère", 20, yPosition);
    yPosition += 10;

    if (criteriaList.length > 0) {
      const topPerformers = criteriaList.slice(0, 10).map(criteria => {
        const sortedByThis = [...rankings]
          .filter(r => r.scores_criteres[criteria]?.score > 0)
          .sort((a, b) => (b.scores_criteres[criteria]?.score || 0) - (a.scores_criteres[criteria]?.score || 0));
        
        const top = sortedByThis[0];
        return [
          criteria.length > 25 ? criteria.substring(0, 25) + "..." : criteria,
          top ? top.societe_nom : "-",
          top ? `${top.scores_criteres[criteria]?.score.toFixed(1)}%` : "-"
        ];
      });

      autoTable(doc, {
        startY: yPosition,
        head: [["Critère", "Meilleure Société", "Score"]],
        body: topPerformers,
        theme: "striped",
        headStyles: { 
          fillColor: primaryColor, 
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 70 },
          2: { cellWidth: 30, halign: "center" }
        },
        margin: { left: 20, right: 20 }
      });
    }

    // Footer on all pages
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      doc.text(
        `Rapport Ranking - ${appelOffre.reference} - Page ${i}/${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    }

    // Save PDF
    const fileName = `Ranking_${appelOffre.reference.replace(/[^a-zA-Z0-9]/g, "_")}_${format(new Date(), "yyyyMMdd")}.pdf`;
    doc.save(fileName);
  };

  return { exportRankingToPdf };
};
